import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as sectorsApi from '@/api/sectors';
import type { SectorFormData } from '@/types/sector';

const SECTORS_KEY = ['sectors'] as const;

export function useSectors() {
  return useQuery({
    queryKey: [...SECTORS_KEY, 'list'],
    queryFn: sectorsApi.getSectors,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSector(id: string | undefined) {
  return useQuery({
    queryKey: [...SECTORS_KEY, 'detail', id],
    queryFn: () => sectorsApi.getSector(id!),
    enabled: !!id,
  });
}

export function useCreateSector() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SectorFormData) => sectorsApi.createSector(data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SECTORS_KEY });
    },
  });
}

export function useUpdateSector() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SectorFormData> }) =>
      sectorsApi.updateSector(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SECTORS_KEY });
    },
  });
}

export function useDeleteSector() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sectorsApi.deleteSector(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SECTORS_KEY });
    },
  });
}

export function useSectorFamilies(
  id: string | undefined,
  page = 1,
  perPage = 25,
  sortBy = 'name',
  sortDir: 'asc' | 'desc' = 'asc',
) {
  return useQuery({
    queryKey: [...SECTORS_KEY, 'families', id, page, perPage, sortBy, sortDir],
    queryFn: () => sectorsApi.getSectorFamilies(id!, { page, per_page: perPage, sort_by: sortBy, sort_dir: sortDir }),
    enabled: !!id,
  });
}

export function useSectorsGeoJson() {
  return useQuery({
    queryKey: [...SECTORS_KEY, 'geojson'],
    queryFn: sectorsApi.getSectorsGeoJson,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateSectorGeometry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, geometry }: { id: string; geometry: GeoJSON.Geometry }) =>
      sectorsApi.updateSectorGeometry(id, geometry),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SECTORS_KEY });
    },
  });
}

export function useDeleteSectorGeometry() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => sectorsApi.deleteSectorGeometry(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SECTORS_KEY });
    },
  });
}

export function useSectorMembersGrouped(id: string | undefined, sort: 'name' | 'address' = 'name') {
  return useQuery({
    queryKey: [...SECTORS_KEY, 'members-grouped', id, sort],
    queryFn: () => sectorsApi.getSectorMembersGrouped(id!, { sort }),
    enabled: !!id,
  });
}

export function useAutoAssignFamilies() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => sectorsApi.autoAssignFamilies(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: SECTORS_KEY });
      await queryClient.invalidateQueries({ queryKey: ['map'] });
      await queryClient.invalidateQueries({ queryKey: ['families'] });
    },
  });
}
